import React from 'react';
import { Field, Form } from 'react-final-form';
import { useScopedI18n } from '@locales/client';
import { depositFiatSchema } from '@/features/wallet-managment/model/schemas';
import { usePostRedirect } from '@/features/wallet-managment/model/hooks';
import {
  FullForm,
  CardForm,
} from '@/features/wallet-managment/ui/fiat-form/ui';
import { Box } from '@/shared/ui/box';
import GiftWallet from '@/shared/assets/gift-wallet.svg';
import Switch from '@/shared/ui/switch/switch';
import { useUser } from '@/shared/providers/user-provider/use-user';
import {
  formatCurrency,
  getCurrencySymbol,
} from '@/shared/libs/format-currency';
import { validateSchema } from '@/shared/libs/validate';
import { NumberInputField } from '@/shared/ui/form/number-input-field';
import {
  BodyTextBoldPrimary,
  BodyTextBoldSecondary,
  BodyTextBoldTertiary,
  BodyTextRegularPrimary,
  BodyTextRegularSecondary,
} from '@/shared/ui/typography';
import {
  DepistAddressContent,
  DepositAddressBonus,
  DepositAddressBonusWrapper,
} from '@/features/wallet-managment/styles';
import {
  DepositButton,
  Payment,
  PaymentPopular,
  Payments,
  WrapperInner,
  WithdrawButton,
} from './styles';

import { useButtonLock } from '@/shared/hooks';

const CURRENCY_FORMAT_CONFIG = {
  maximumFractionDigits: 0,
  minimumFractionDigits: 0,
};

export const FiatForm = ({
  settings,
  currentBonus,
  createDepositFiatTrigger,
  bonusActive,
  setBonusActive,
}) => {
  const { user } = useUser();
  const currencySymbol = getCurrencySymbol(user.currency);
  const t = useScopedI18n('wallet');
  const tBonus = useScopedI18n('bonus');
  const executeRedirect = usePostRedirect();
  const { isLocked, lock, unlock } = useButtonLock();

  const [formDeposit, setformDeposit] = React.useState({});
  const [nextStep, setNextStep] = React.useState(false);

  const submitDeposit = async (values) => {
    const { amount: formAmount } = formDeposit;
    const { amount: valuesAmount, ...restValues } = values;
    const amount = formAmount || valuesAmount || 0;

    const params = {
      method: settings.method,
      bonus: bonusActive ? currentBonus?.id : 0,
      amount,
      ...(formAmount && restValues && { fields: restValues }),
    };
    lock();
    try {
      const response = await createDepositFiatTrigger(params);
      executeRedirect(response, unlock);
    } catch (error) {
      unlock();
      console.error('Deposit error:', error);
    }
  };

  const formComponents = React.useMemo(
    () => ({
      fullForm: (
        <FullForm
          handleSubmit={submitDeposit}
          settings={settings}
          isLocked={isLocked}
        />
      ),
      holderCardForm: (
        <CardForm
          handleSubmit={submitDeposit}
          settings={settings}
          isLocked={isLocked}
          withHolder
        />
      ),
    }),
    [settings, submitDeposit]
  );

  const settingMetodsFirst = settings?.fields?.[0];
  const mapComponent = formComponents[settingMetodsFirst];
  const showSecondStep = nextStep && mapComponent;

  const handleCheckBonus = (checked) => setBonusActive(checked);

  const schema = depositFiatSchema(settings?.minAmount, settings?.maxAmount);
  const validate = validateSchema(schema);

  const bonusAmount = currentBonus?.percent
    ? Math.min(
        (formDeposit.amount * currentBonus.percent) / 100,
        currentBonus.maxDeposit
      )
    : 0;

  const popularAmount = settings?.amountOptions?.find(
    (option) => option.isPopular
  )?.amount;

  return (
    <WrapperInner>
      {!showSecondStep && (
        <>
          <Box>
            <BodyTextRegularSecondary color="#AFAFAF">
              {t('fiat.amount.title')}
            </BodyTextRegularSecondary>
          </Box>

          <Box $paddingTop={16}>
            <Form
              validate={validate}
              initialValues={{ amount: popularAmount }}
              onSubmit={submitDeposit}
            >
              {(props) => {
                const { amount } = props.values;
                const formattedAmount = formatCurrency(
                  amount,
                  { currency: user.currency, currencySymbol },
                  CURRENCY_FORMAT_CONFIG
                );
                const disabledBtbn =
                  +settings?.minAmount > +amount ||
                  +settings?.maxAmount < +amount ||
                  isLocked;

                const handleBtnClick = () => {
                  if (
                    ['fullForm', 'holderCardForm'].includes(settingMetodsFirst)
                  ) {
                    setformDeposit(props.values);
                    setNextStep(true);
                  } else {
                    props.form.submit();
                  }
                };

                return (
                  <>
                    <Field name="amount" component={NumberInputField} />

                    <Box
                      $display={'flex'}
                      $flexDirection={'column'}
                      $justifyContent={'space-between'}
                    >
                      <Payments>
                        {settings?.amountOptions?.map((option) => (
                          <Payment
                            key={option.amount}
                            $isPopular={amount === option.amount}
                            onClick={() =>
                              props.form.change('amount', option.amount)
                            }
                          >
                            {option.isPopular && (
                              <PaymentPopular>
                                <BodyTextBoldTertiary>
                                  {t('fiat.popular')}
                                </BodyTextBoldTertiary>
                              </PaymentPopular>
                            )}
                            <BodyTextBoldSecondary>
                              {option.amount} {currencySymbol}
                            </BodyTextBoldSecondary>
                          </Payment>
                        ))}
                      </Payments>

                      {settings?.rate !== 1 && (
                        <Box>
                          <BodyTextRegularSecondary color="#AFAFAF">
                            1 {user.currency} ={' '}
                            {formatCurrency(1 * settings.rate, {
                              currency: user.currency,
                              currencySymbol: settings.currencySymbol,
                            })}
                          </BodyTextRegularSecondary>
                        </Box>
                      )}

                      <Box>
                        <Box $display="flex" $justifyContent="center" $gap={4}>
                          <BodyTextRegularSecondary color="#AFAFAF">
                            {t('fiat.min_deposit')}
                          </BodyTextRegularSecondary>
                          <BodyTextBoldSecondary>
                            {formatCurrency(settings.minAmount, {
                              currency: user.currency,
                              currencySymbol,
                            })}
                          </BodyTextBoldSecondary>
                        </Box>

                        <Box $display="flex" $justifyContent="center" $gap={4}>
                          <BodyTextRegularSecondary color="#AFAFAF">
                            {t('fiat.max_deposit')}
                          </BodyTextRegularSecondary>
                          <BodyTextBoldSecondary>
                            {formatCurrency(settings.maxAmount, {
                              currency: user.currency,
                              currencySymbol,
                            })}
                          </BodyTextBoldSecondary>
                        </Box>
                      </Box>

                      <Box
                        $position={['absolute', 'static']}
                        $bottom="0"
                        $left="0"
                        $width="100%"
                        $padding={['18px', 0]}
                        $display="flex"
                        $flexDirection="column"
                        $gap={24}
                        $marginTop={[0, '24px']}
                      >
                        {currentBonus?.id && (
                          <DepositAddressBonusWrapper $active={bonusActive}>
                            <DepositAddressBonus $active={bonusActive}>
                              <Box>
                                <GiftWallet />
                              </Box>
                              <DepositButton $active={bonusActive}>
                                <Box
                                  $display="flex"
                                  $justifyContent={'center'}
                                  $gap={6}
                                  $alignItems={'center'}
                                >
                                  <BodyTextRegularPrimary>
                                    {currentBonus.type !== 'freespin' ? (
                                      t('bonus.title', {
                                        bonus: (
                                          <BodyTextBoldPrimary color="#FFD60A">
                                            {formatCurrency(bonusAmount, {
                                              currency: user.currency,
                                              currencySymbol,
                                            })}
                                          </BodyTextBoldPrimary>
                                        ),
                                      })
                                    ) : (
                                      <BodyTextBoldPrimary>
                                        + {currentBonus.spins} FS
                                      </BodyTextBoldPrimary>
                                    )}
                                  </BodyTextRegularPrimary>
                                </Box>
                              </DepositButton>
                              {!bonusActive && t('bonus.bonus_enable')}
                              <Box $position={'relative'} $zIndex={'1'}>
                                <Switch
                                  onCheckedChange={handleCheckBonus}
                                  checked={bonusActive}
                                />
                              </Box>
                            </DepositAddressBonus>

                            <DepistAddressContent>
                              {currentBonus.spins ? (
                                <>
                                  <BodyTextRegularSecondary color="#AEB8D1">
                                    {tBonus('card.spins')}
                                  </BodyTextRegularSecondary>
                                  <BodyTextBoldSecondary>
                                    {currentBonus.spins}
                                  </BodyTextBoldSecondary>
                                </>
                              ) : null}

                              {currentBonus.percent ? (
                                <>
                                  <BodyTextRegularSecondary color="#AEB8D1">
                                    {t('bonus.percent')}
                                  </BodyTextRegularSecondary>
                                  <BodyTextBoldSecondary>
                                    {currentBonus.percent}%
                                  </BodyTextBoldSecondary>
                                </>
                              ) : null}
                            </DepistAddressContent>
                          </DepositAddressBonusWrapper>
                        )}

                        <WithdrawButton
                          type="button"
                          disabled={disabledBtbn}
                          $isLoading={isLocked}
                          variant={!disabledBtbn ? 'orangeGradient' : 'gray'}
                          onClick={handleBtnClick}
                        >
                          <BodyTextBoldSecondary>
                            {t('fiat.amount.button_action', {
                              amount: formattedAmount,
                            })}
                          </BodyTextBoldSecondary>
                        </WithdrawButton>
                      </Box>
                    </Box>
                  </>
                );
              }}
            </Form>
          </Box>
        </>
      )}

      {showSecondStep && mapComponent}
    </WrapperInner>
  );
};
